import * as Yup from 'yup';
import { getMaxValidation, getRequiredMessage } from 'views/declarations/utils/validation-utils';
import {
    CODES_REQUIRING_AMENDMENT_LIST,
    CODE_REQUIRING_AMENDMENT,
    CODES_REQUIRING_ATTACHMENT,
    CODES_REQUIRING_TEXT,
    MAX_INFORMATION_ENTRIES,
} from './types';

type Context = { attachmentCount?: number };

const codeIn = (codes: string[], code: unknown) => typeof code === 'string' && codes.includes(code);

export const validationSchema = Yup.object({
    acceptedAmendmentRequestId: Yup.string().nullable(),
    additionalInformation: Yup.array()
        .of(
            Yup.object({
                code: getMaxValidation('code', 17)
                    .nullable()
                    .test('code-required-for-text', getRequiredMessage('responseCode'), function (code) {
                        return !this.parent?.text?.trim() || Boolean(code);
                    }),
                text: getMaxValidation('text', 512)
                    .nullable()
                    .test('text-required-for-code', 'This response code requires an explanation.', function (text) {
                        return !codeIn(CODES_REQUIRING_TEXT, this.parent?.code) || Boolean(text?.trim());
                    }),
            })
        )
        .max(MAX_INFORMATION_ENTRIES, `No more than ${MAX_INFORMATION_ENTRIES} entries are allowed.`)
        .test('attachment-required-for-code', '', function (entries) {
            const { attachmentCount = 0 } = (this.options.context ?? {}) as Context;
            const index = (entries ?? []).findIndex((entry) => codeIn(CODES_REQUIRING_ATTACHMENT, entry?.code));
            if (index === -1 || attachmentCount > 0) return true;
            return this.createError({
                path: `${this.path}[${index}].code`,
                message: 'This response code requires at least one attached document.',
            });
        })
        .test('amendment-required-for-code', '', function (entries) {
            const index = (entries ?? []).findIndex((entry) => codeIn(CODES_REQUIRING_AMENDMENT_LIST, entry?.code));
            if (index === -1 || this.parent?.acceptedAmendmentRequestId) return true;
            return this.createError({
                path: `${this.path}[${index}].code`,
                message: `Code ${CODE_REQUIRING_AMENDMENT} must reference an accepted amendment.`,
            });
        })
        // The backend requires every RFI/AMD referral response to carry information or evidence.
        .test('answer-required', '', function (entries) {
            const { attachmentCount = 0 } = (this.options.context ?? {}) as Context;
            const hasInformation = (entries ?? []).some((entry) => entry?.code || entry?.text?.trim());
            if (hasInformation || attachmentCount > 0) return true;
            return this.createError({
                path: `${this.path}[0].code`,
                message: getRequiredMessage('responseCode'),
            });
        }),
});
